import { Rect } from '../../primitives/rect';
export class MatrixModel {
    constructor(model) {
        this.flowchartModel = model;
        this.matrix = [];
        this.rowOffset = [];
        this.rowMaxDimension = [];
        this.siblingModel = null;
    }
    /**
     * @private
     * @returns {void} - Arranges the elements in the flowchart layout
     */
    arrangeElements() {
        if (this.flowchartModel === null) {
            return;
        }
        const layoutSettings = this.flowchartModel.layout;
        const isHorizontal = layoutSettings.orientation === 'LeftToRight';
        const spacing = isHorizontal ? layoutSettings.verticalSpacing : layoutSettings.horizontalSpacing;
        this.groupLayoutCells();
        this.createMatrixCells();
        for (const matrixRow of this.matrix) {
            for (let i = 1; i < matrixRow.length; i++) {
                const cell = matrixRow[parseInt(i.toString(), 10)];
                const prevCell = matrixRow[i - 1];
                cell.offset += prevCell.offset + (prevCell.size / 2) + spacing + (cell.size / 2);
            }
        }
        for (const root of this.matrix[0]) {
            this.arrangeMatrix(root, null);
        }
        for (const row of this.matrix) {
            for (let i = 0; i < row.length; i++) {
                const cell = row[parseInt(i.toString(), 10)];
                if (cell.visitedParents.length > 1) {
                    let firstParent = cell.visitedParents[0];
                    let lastParent = cell.visitedParents[cell.visitedParents.length - 1];
                    const firstVertexParent = this.findParentVertexCellGroup(firstParent);
                    const lastVertexParent = this.findParentVertexCellGroup(lastParent);
                    if (firstParent !== firstVertexParent && firstVertexParent.offset < firstParent.offset) {
                        firstParent = firstVertexParent;
                    }
                    if (lastParent !== lastVertexParent && lastVertexParent.offset > lastParent.offset) {
                        lastParent = firstVertexParent;
                    }
                    let newOffset = (firstParent.offset + lastParent.offset) / 2;
                    const interVertex = cell.cells.find((c) => 'internalInEdges' in c && 'internalOutEdges' in c);
                    if (this.flowchartModel.layout.yesBranchDirection === 'SameAsFlow') {
                        const tempVisitedParents = [...cell.visitedParents];
                        if (interVertex && interVertex.cell.isYesChild) {
                            for (const tempParent of tempVisitedParents) {
                                const tempParentVertex = tempParent.cells.find((c) => 'internalInEdges' in c && 'internalOutEdges' in c);
                                if (!tempParentVertex) {
                                    newOffset = tempParent.offset;
                                    break;
                                }
                            }
                        }
                        else {
                            if (this.flowchartModel.layout.noBranchDirection === 'LeftInFlow') {
                                tempVisitedParents.reverse();
                            }
                            for (const tempParent of tempVisitedParents) {
                                const tempParentVertex = tempParent.cells.find((c) => 'internalInEdges' in c && 'internalOutEdges' in c);
                                if (tempParentVertex) {
                                    if (tempParentVertex.cell.isYesChild) {
                                        newOffset = tempParent.offset;
                                        break;
                                    }
                                }
                                else {
                                    const tempSuperParent = this.findParentVertexCellGroup(tempParent);
                                    if (tempSuperParent) {
                                        const superParentVertex = tempSuperParent.cells.find((c) => 'internalInEdges' in c && 'internalOutEdges' in c);
                                        if (superParentVertex && superParentVertex.cell.isYesChild) {
                                            newOffset = tempParent.offset;
                                            break;
                                        }
                                    }
                                }
                            }
                        }
                    }
                    else if (this.flowchartModel.layout.noBranchDirection === 'SameAsFlow') {
                        const tempVisitedParents = [...cell.visitedParents];
                        if (interVertex && interVertex.cell.isNoChild) {
                            for (const tempParent of tempVisitedParents) {
                                const tempParentVertex = tempParent.cells.find((c) => 'internalInEdges' in c && 'internalOutEdges' in c);
                                if (!tempParentVertex) {
                                    newOffset = tempParent.offset;
                                    break;
                                }
                            }
                        }
                        else {
                            if (this.flowchartModel.layout.yesBranchDirection === 'LeftInFlow') {
                                tempVisitedParents.reverse();
                            }
                            for (const tempParent of tempVisitedParents) {
                                const tempParentVertex = tempParent.cells.find((c) => 'internalInEdges' in c && 'internalOutEdges' in c);
                                if (tempParentVertex) {
                                    if (tempParentVertex.cell.isNoChild) {
                                        newOffset = tempParent.offset;
                                        break;
                                    }
                                }
                                else {
                                    const tempSuperParent = this.findParentVertexCellGroup(tempParent);
                                    if (tempSuperParent) {
                                        const superParentVertex = tempSuperParent.cells.find((c) => 'internalInEdges' in c && 'internalOutEdges' in c);
                                        if (superParentVertex && superParentVertex.cell.isNoChild) {
                                            newOffset = tempParent.offset;
                                            break;
                                        }
                                    }
                                }
                            }
                        }
                    }
                    const availOffsetMin = cell.initialOffset;
                    const availOffsetMax = cell.offset;
                    if (availOffsetMax !== availOffsetMin) {
                        if (newOffset >= availOffsetMin && newOffset <= availOffsetMax) {
                            this.translateMatrixCells(newOffset - cell.offset, cell);
                        }
                        else if (newOffset < availOffsetMin) {
                            this.translateMatrixCells(availOffsetMin - cell.offset, cell);
                        }
                    }
                }
            }
        }
        this.setXYForMatrixCell();
    }
    arrangeMatrix(cell, parent) {
        const layoutSettings = this.flowchartModel.layout;
        const isHorizontal = layoutSettings.orientation === 'LeftToRight';
        const spacing = isHorizontal ? layoutSettings.verticalSpacing : layoutSettings.horizontalSpacing;
        const matrixRow = this.matrix[cell.level];
        const matrixIndex = matrixRow.indexOf(cell);
        if (cell.visitedParents.length) {
            if (cell.visitedParents.length === 1) {
                cell.initialOffset = cell.offset;
            }
            if (matrixIndex + 1 < matrixRow.length) {
                const nextCell = matrixRow[matrixIndex + 1];
                if (nextCell.visitedParents.length) {
                    if (cell.visitedParents.indexOf(parent) === -1) {
                        if (cell.level !== parent.level) {
                            cell.visitedParents.push(parent);
                            parent.ignoredChildren.push(cell);
                        }
                        return;
                    }
                }
            }
        }
        if (!cell.children.length) {
            let validOffset = cell.offset;
            if (matrixIndex > 0) {
                const prevCell = matrixRow[matrixIndex - 1];
                validOffset = prevCell.offset + (prevCell.size / 2) + spacing + (cell.size / 2);
            }
            this.shiftMatrixCells(validOffset - cell.offset, cell);
        }
        else {
            for (const matrixCellChild of cell.children) {
                if (cell.visitedChildren.indexOf(matrixCellChild)) {
                    this.arrangeMatrix(matrixCellChild, cell);
                    if (cell.level !== matrixCellChild.level) {
                        cell.visitedChildren.push(matrixCellChild);
                    }
                    else {
                        cell.loopChildren.push(matrixCellChild);
                    }
                }
            }
            if (cell.visitedChildren.length) {
                let children = [...cell.visitedChildren];
                for (const cellIgnoredChild of cell.ignoredChildren) {
                    children = children.filter((child) => child !== cellIgnoredChild);
                }
                if (children.length) {
                    const firstChild = children[0];
                    const lastChild = children[children.length - 1];
                    const x1 = firstChild.offset;
                    const x2 = lastChild.offset;
                    let newOffset = (x1 + x2) / 2;
                    if (cell.cells.length) {
                        const interVertex = cell.cells.filter((c) => 'internalInEdges' in c && 'internalOutEdges' in c)[0];
                        const firstChildVertex = firstChild.cells.filter((c) => 'internalInEdges' in c && 'internalOutEdges' in c)[0];
                        const lastChildVertex = lastChild.cells.filter((c) => 'internalInEdges' in c && 'internalOutEdges' in c)[0];
                        if (interVertex && interVertex.cell.isDecisionNode) {
                            if (this.flowchartModel.layout.yesBranchDirection === 'SameAsFlow') {
                                if (firstChildVertex) {
                                    newOffset = firstChildVertex.cell.isYesChild ? firstChild.offset : lastChild.offset;
                                }
                                else if (lastChildVertex) {
                                    newOffset = lastChildVertex.cell.isYesChild ? lastChild.offset : firstChild.offset;
                                }
                            }
                            else if (this.flowchartModel.layout.noBranchDirection === 'SameAsFlow') {
                                if (firstChildVertex) {
                                    newOffset = firstChildVertex.cell.isNoChild ? firstChild.offset : lastChild.offset;
                                }
                                else if (lastChildVertex) {
                                    newOffset = lastChildVertex.cell.isNoChild ? lastChild.offset : firstChild.offset;
                                }
                            }
                        }
                    }
                    if (newOffset < cell.offset) {
                        this.shiftMatrixCells(cell.offset - newOffset, firstChild, true, cell);
                    }
                    else if (newOffset > cell.offset) {
                        this.shiftMatrixCells(newOffset - cell.offset, cell);
                    }
                }
            }
        }
        if (cell.visitedParents.indexOf(parent) === -1) {
            if (parent !== null && cell.level !== parent.level) {
                cell.visitedParents.push(parent);
            }
        }
    }
    shiftMatrixCells(value, startingCell, shiftChildren = false, parentCell = null) {
        if (value !== 0) {
            const matrixRow = this.matrix[startingCell.level];
            const index = matrixRow.indexOf(startingCell);
            for (let i = index; i < matrixRow.length; i++) {
                matrixRow[parseInt(i.toString(), 10)].offset += value;
            }
            if (shiftChildren) {
                if (startingCell.visitedChildren.length) {
                    this.shiftMatrixCells(value, startingCell.visitedChildren[0], true, startingCell);
                }
                else {
                    let i = 1;
                    let nextSiblingWithChild = null;
                    while (index + i < matrixRow.length) {
                        const nextCell = matrixRow[index + i];
                        if (parentCell !== null && nextCell.visitedParents.indexOf(parentCell) !== -1) {
                            if (nextCell.visitedChildren.length) {
                                nextSiblingWithChild = nextCell;
                            }
                            else {
                                i++;
                                continue;
                            }
                        }
                        break;
                    }
                    if (nextSiblingWithChild !== null) {
                        this.shiftMatrixCells(value, nextSiblingWithChild.visitedChildren[0], true, nextSiblingWithChild);
                    }
                }
            }
        }
    }
    findParentVertexCellGroup(cell) {
        if ('internalInEdges' in cell.cells[0] && 'internalOutEdges' in cell.cells[0]) {
            return cell;
        }
        if (cell.parents.length) {
            return this.findParentVertexCellGroup(cell.parents[0]);
        }
        return cell;
    }
    translateMatrixCells(value, cell) {
        if (value !== 0) {
            cell.offset += value;
            if (cell.visitedChildren.length) {
                for (const child of cell.visitedChildren) {
                    this.translateMatrixCells(value, child);
                }
                for (const loopChild of cell.loopChildren) {
                    this.translateMatrixCells(value, loopChild);
                }
            }
        }
    }
    getObjectValues(obj) {
        const values = [];
        for (const key in obj) {
            if (Object.prototype.hasOwnProperty.call(obj, key)) {
                values.push(obj[`${key}`]);
            }
        }
        return values;
    }
    setXYForMatrixCell() {
        const layoutSettings = this.flowchartModel.layout;
        const isHorizontal = layoutSettings.orientation === 'LeftToRight';
        const spacing = isHorizontal ? layoutSettings.verticalSpacing : layoutSettings.horizontalSpacing;
        let siblingSize = 0;
        if (this.siblingModel) {
            const rowMaxValues = this.getObjectValues(this.rowMaxDimension);
            const maxRowValue = Math.max(...rowMaxValues);
            siblingSize = this.siblingModel.getSiblingDimension(maxRowValue);
        }
        for (const matrixRow1 of this.getObjectValues(this.matrix)) {
            for (const matrixCell of matrixRow1) {
                let start = matrixCell.offset - (matrixCell.size / 2);
                if (siblingSize !== 0) {
                    start += siblingSize + spacing;
                }
                for (const cell of matrixCell.cells) {
                    if ('internalInEdges' in cell && 'internalOutEdges' in cell) {
                        const internalVertex = cell;
                        const width = internalVertex.cell.geometry.width;
                        const height = internalVertex.cell.geometry.height;
                        if (isHorizontal) {
                            internalVertex.cell.geometry = new Rect(this.rowOffset[matrixCell.level] - (width / 2), start, width, height);
                        }
                        else {
                            internalVertex.cell.geometry = new Rect(start, this.rowOffset[matrixCell.level] - (height / 2), width, height);
                        }
                        start += (isHorizontal ? height : width) + spacing;
                    }
                    else if ('edges' in cell) {
                        const internalEdges = cell;
                        let isContainSiblingVertex = internalEdges.isReversed;
                        if (!isContainSiblingVertex) {
                            const parent = matrixCell.visitedParents[0];
                            if (parent) {
                                for (const child of parent.visitedChildren) {
                                    if (child.cells.some((c) => 'internalInEdges' in c && 'internalOutEdges' in c)) {
                                        isContainSiblingVertex = true;
                                        break;
                                    }
                                }
                            }
                        }
                        const lineWidth = 1;
                        const edgeSpacing = 5;
                        for (const internalConnector of internalEdges.edges) {
                            if (isContainSiblingVertex) {
                                let pt = { x: start + (lineWidth / 2.0), y: this.rowOffset[matrixCell.level] };
                                if (isHorizontal) {
                                    pt = { x: this.rowOffset[matrixCell.level], y: start + (lineWidth / 2.0) };
                                }
                                if (this.flowchartModel.layout.edgesMapper.has(internalConnector)) {
                                    this.flowchartModel.layout.edgesMapper.get(internalConnector).push(pt);
                                    this.flowchartModel.layout.loopedgesMapper.set(internalConnector, internalEdges.isReversed);
                                }
                            }
                            start += lineWidth + edgeSpacing;
                        }
                        start += spacing;
                    }
                }
            }
        }
    }
    getSiblingDimension(maxHeight) {
        const layoutSettings = this.flowchartModel.layout;
        const isHorizontal = layoutSettings.orientation === 'LeftToRight';
        const spacing = isHorizontal ? layoutSettings.horizontalSpacing : layoutSettings.verticalSpacing;
        let commonRowIndex = 0;
        for (const key in this.rowMaxDimension) {
            if (Object.prototype.hasOwnProperty.call(this.rowMaxDimension, key)) {
                const value = this.rowMaxDimension[`${key}`];
                if (value < maxHeight) {
                    commonRowIndex = parseInt(key, 10);
                }
                else {
                    break;
                }
            }
        }
        if (this.rowMaxDimension[parseInt(commonRowIndex.toString(), 10)] + spacing <= maxHeight
            && this.rowMaxDimension[commonRowIndex + 1]) {
            commonRowIndex++;
        }
        const commonRows = [];
        for (const key in this.matrix) {
            if (Object.prototype.hasOwnProperty.call(this.matrix, key) && parseInt(key, 10) <= commonRowIndex) {
                commonRows[`${key}`] = this.matrix[`${key}`];
            }
        }
        let maxSize = 0;
        for (const key in commonRows) {
            if (Object.prototype.hasOwnProperty.call(commonRows, key)) {
                const matrixRow1 = commonRows[`${key}`];
                if (matrixRow1.length) {
                    const firstCell = matrixRow1[0].cells[0];
                    let rowStart = 0;
                    if (firstCell && 'cell' in firstCell) {
                        const geometry = firstCell.cell.geometry;
                        rowStart = isHorizontal ? geometry.y : geometry.x;
                    }
                    else if (firstCell && 'edges' in firstCell) {
                        const internalConnector = firstCell.edges[firstCell.edges.length - 1];
                        const edgePts = this.flowchartModel.layout.edgesMapper.get(internalConnector);
                        if (edgePts.length) {
                            rowStart = isHorizontal ? edgePts[0].y : edgePts[0].x;
                        }
                    }
                    let rowEnd = 0;
                    const lastCell = matrixRow1[matrixRow1.length - 1]
                        .cells[matrixRow1[matrixRow1.length - 1].cells.length - 1];
                    if (lastCell && 'cell' in lastCell) {
                        const geometry = lastCell.cell.geometry;
                        rowEnd = isHorizontal ? geometry.y + geometry.height : geometry.x + geometry.width;
                    }
                    else if (lastCell && 'edges' in lastCell) {
                        const internalConnector = lastCell.edges[lastCell.edges.length - 1];
                        const edgePts = this.flowchartModel.layout.edgesMapper.get(internalConnector);
                        if (edgePts.length) {
                            rowEnd = isHorizontal ? edgePts[0].y : edgePts[0].x;
                        }
                    }
                    maxSize = Math.max(maxSize, rowEnd - rowStart);
                }
            }
        }
        return maxSize;
    }
    createMatrixCells() {
        const layoutSettings = this.flowchartModel.layout;
        const isHorizontal = layoutSettings.orientation === 'LeftToRight';
        const spacing = isHorizontal ? layoutSettings.verticalSpacing : layoutSettings.horizontalSpacing;
        const spacingInverse = isHorizontal ? layoutSettings.horizontalSpacing : layoutSettings.verticalSpacing;
        const rank = this.flowchartModel.ranks;
        const ranks = Array.from(rank.values());
        const matrixCellMapper = {};
        let matrixRowOffset = -spacingInverse;
        for (let j = ranks.length - 1; j >= 0; j--) {
            let maxDimension = 0.0;
            const index = (ranks.length - 1) - j;
            const rank = [...ranks[parseInt(j.toString(), 10)]];
            // Creating new row and adding it to matrix
            const matrixRow = [];
            this.matrix[parseInt(index.toString(), 10)] = matrixRow;
            // Creating new row mapper
            const tempMatrixRow = {};
            matrixCellMapper[parseInt(index.toString(), 10)] = tempMatrixRow;
            while (rank.length > 0) {
                const layoutCell = rank[0];
                const matrixCell = {
                    parents: [],
                    children: [],
                    visitedParents: [],
                    visitedChildren: [],
                    ignoredChildren: [],
                    loopChildren: [],
                    cells: [],
                    level: index,
                    initialOffset: 0,
                    size: 0,
                    offset: 0
                };
                matrixRow.push(matrixCell);
                if ('internalInEdges' in layoutCell && 'internalOutEdges' in layoutCell) {
                    matrixCell.cells.push(layoutCell);
                    if (layoutCell.identicalSibling) {
                        for (let i = 0; i < rank.length; i++) {
                            const internalVertex = rank[parseInt(i.toString(), 10)];
                            if ('internalInEdges' in internalVertex && 'internalOutEdges' in internalVertex) {
                                if (layoutCell.identicalSibling.indexOf(internalVertex.id) !== -1) {
                                    matrixCell.cells.push(internalVertex);
                                    if (matrixCell.cells.length > layoutCell.identicalSibling.length) {
                                        break;
                                    }
                                }
                            }
                        }
                    }
                    const cells = matrixCell.cells;
                    for (let i = 0; i < cells.length; i++) {
                        const internalVertex = cells[parseInt(i.toString(), 10)];
                        if ('internalInEdges' in internalVertex && 'internalOutEdges' in internalVertex) {
                            const geometry = internalVertex.cell.geometry;
                            matrixCell.size += isHorizontal ? geometry.height : geometry.width;
                            maxDimension = Math.max(maxDimension, isHorizontal ? geometry.width : geometry.height);
                            tempMatrixRow[internalVertex.id] = matrixCell;
                            if (internalVertex.internalInEdges.length) {
                                const internalInEdges = internalVertex.internalInEdges;
                                for (let j = 0; j < internalInEdges.length; j++) {
                                    const internalEdges = internalInEdges[parseInt(j.toString(), 10)];
                                    if (internalEdges.isReversed) {
                                        continue;
                                    }
                                    let key = null;
                                    if (matrixCellMapper[index - 1] &&
                                        Object.prototype.hasOwnProperty.call(matrixCellMapper[index - 1], internalEdges.ids)) {
                                        key = internalEdges.ids;
                                    }
                                    else if (matrixCellMapper[index - 1] &&
                                        Object.prototype.hasOwnProperty.call(matrixCellMapper[index - 1], internalEdges.source.id)) {
                                        key = internalEdges.source.id;
                                    }
                                    if (key !== null) {
                                        const parentMatrixCell = matrixCellMapper[index - 1][`${key}`];
                                        if (matrixCell.parents.indexOf(parentMatrixCell) === -1) {
                                            matrixCell.parents.push(parentMatrixCell);
                                        }
                                        if (parentMatrixCell.children.indexOf(matrixCell) === -1) {
                                            if (parentMatrixCell.children.length) {
                                                if (parentMatrixCell.children[0].level === parentMatrixCell.level) {
                                                    parentMatrixCell.children.unshift(matrixCell);
                                                }
                                                else {
                                                    parentMatrixCell.children.push(matrixCell);
                                                }
                                            }
                                            else {
                                                parentMatrixCell.children.push(matrixCell);
                                            }
                                        }
                                    }
                                }
                            }
                            rank.splice(rank.indexOf(internalVertex), 1);
                        }
                    }
                    matrixCell.size += (matrixCell.cells.length - 1) * spacing;
                }
                else if ('edges' in layoutCell) {
                    matrixCell.cells.push(layoutCell);
                    const cells = matrixCell.cells;
                    for (let i = 0; i < cells.length; i++) {
                        const internalEdge = cells[parseInt(i.toString(), 10)];
                        if ('edges' in internalEdge && internalEdge.edges) {
                            const lineWidth = 1;
                            const edgeSpacing = 5;
                            let cellSize = -edgeSpacing;
                            for (let j = 0; j < internalEdge.edges.length; j++) {
                                const internalConnector = internalEdge.edges[parseInt(j.toString(), 10)];
                                cellSize += lineWidth + edgeSpacing;
                            }
                            matrixCell.size += cellSize;
                        }
                        let key = null;
                        if (internalEdge.isReversed) {
                            if (matrixCellMapper[parseInt(index.toString(), 10)][internalEdge.ids]) {
                                key = internalEdge.ids;
                            }
                            else if (matrixCellMapper[parseInt(index.toString(), 10)][internalEdge.source.id]) {
                                key = internalEdge.source.id;
                            }
                        }
                        else {
                            if (matrixCellMapper[index - 1][internalEdge.ids]) {
                                key = internalEdge.ids;
                            }
                            else if (matrixCellMapper[index - 1][internalEdge.source.id]) {
                                key = internalEdge.source.id;
                            }
                        }
                        if (key !== null) {
                            const parentMatrixCell = matrixCellMapper[internalEdge.isReversed ?
                                index : index - 1][`${key}`];
                            if (matrixCell.parents.indexOf(parentMatrixCell) === -1) {
                                matrixCell.parents.push(parentMatrixCell);
                            }
                            if (parentMatrixCell.children.indexOf(matrixCell) === -1) {
                                parentMatrixCell.children.push(matrixCell);
                            }
                        }
                        tempMatrixRow[internalEdge.ids] = matrixCell;
                        rank.splice(rank.indexOf(internalEdge), 1);
                    }
                    matrixCell.size += (matrixCell.cells.length - 1) * spacing;
                }
            }
            this.rowOffset[parseInt(index.toString(), 10)] = matrixRowOffset + (maxDimension / 2) + spacingInverse;
            this.rowMaxDimension[parseInt(index.toString(), 10)] = this.rowOffset[parseInt(index.toString(), 10)] + maxDimension / 2;
            matrixRowOffset += maxDimension + spacingInverse;
        }
    }
    groupLayoutCells() {
        const rank = this.flowchartModel.ranks;
        const ranks = Array.from(rank.values());
        ranks.reverse();
        for (let j = ranks.length - 1; j >= 0; j--) {
            const vertices = ranks[parseInt(j.toString(), 10)].filter((v) => 'internalInEdges' in v && 'internalOutEdges' in v);
            const edges = ranks[parseInt(j.toString(), 10)].filter((e) => 'edges' in e && 'edges' in e);
            while (vertices.length > 1) {
                const vertex1 = vertices[0];
                if (vertex1.cell.isYesChild || vertex1.cell.isNoChild) {
                    vertices.shift();
                    continue;
                }
                const parentSet1 = vertex1.internalInEdges.map((e) => e.source.id);
                const childSet1 = vertex1.internalOutEdges.map((e) => e.target.id);
                while (vertices.length > 1) {
                    const vertex2 = vertices[1];
                    const parentSet2 = vertex2.internalInEdges.map((e) => e.source.id);
                    const childSet2 = vertex2.internalOutEdges.map((e) => e.target.id);
                    const parentEquals = this.compareLists(parentSet1, parentSet2);
                    const childEquals = this.compareLists(childSet1, childSet2);
                    if (parentEquals && childEquals) {
                        this.updateMutualSharing(vertices[0], vertex2.id);
                        this.updateMutualSharing(vertices[1], vertex1.id);
                        vertices.splice(1, 1);
                        continue;
                    }
                    break;
                }
                vertices.shift();
            }
            while (edges.length > 1) {
                const internalEdge = edges[0];
                const parentSet = internalEdge.source;
                const childSet = internalEdge.target;
                if (parentSet.identicalSibling) {
                    const groupEdges = edges.filter((e) => e.target === childSet);
                    for (const internalEdges of groupEdges) {
                        if ((parentSet.identicalSibling).indexOf(internalEdges.source.id) !== -1) {
                            internalEdges.source.identicalSibling = null;
                        }
                    }
                    internalEdge.source.identicalSibling = null;
                }
                edges.shift();
            }
        }
    }
    updateMutualSharing(cell, id) {
        if (cell.identicalSibling) {
            cell.identicalSibling.push(id);
        }
        else {
            cell.identicalSibling = [id];
        }
    }
    compareLists(list1, list2) {
        const newList1 = list1.slice();
        const newList2 = list2.slice();
        if (newList1.length === newList2.length) {
            if (newList1.length === 0) {
                return true;
            }
            for (const o of newList2) {
                if (newList1.indexOf(o) === -1) {
                    return false;
                }
            }
            return true;
        }
        return false;
    }
}
